import useReveal from '../hooks/useReveal';
import SplitText from './SplitText';

/* ── Reveal wrapper ──
   variant: 'up' | 'left' | 'right' | 'scale' | 'fade'
   as: any HTML tag (defaults to 'div')
*/
export function Reveal({ children, delay = 0, style, variant = 'up', as: Tag = 'div', className = '' }) {
  const [ref, vis] = useReveal(0.1, '-30px');
  return (
    <Tag
      ref={ref}
      className={`reveal reveal-${variant}${vis ? ' in' : ''} ${className}`.trim()}
      style={{ transitionDelay: `${delay}ms`, ...style }}
    >
      {children}
    </Tag>
  );
}

/* ── Eyebrow ── */
export function Eyebrow({ children, light }) {
  return (
    <span className={`eyebrow${light ? ' light' : ''}`}>{children}</span>
  );
}

/* ── Section Head ── */
export function SectionHead({ eyebrow, h2, p, center }) {
  const align = center ? 'center' : 'left';

  // Split h2 into two halves — first half dark, second half purple
  const renderSplitH2 = (text) => {
    if (!text) return null;
    const words = text.split(' ');
    const mid = Math.ceil(words.length / 2);
    const firstHalf = words.slice(0, mid).join(' ');
    const secondHalf = words.slice(mid).join(' ');
    return (
      <h2 className="section-head-h2">
        <span className="sh2-dark">{firstHalf}</span>
        {secondHalf && <> <span className="sh2-purple">{secondHalf}</span></>}
      </h2>
    );
  };

  return (
    <div className={`section-head${center ? ' center' : ''}`}>
      {eyebrow && (
        <Eyebrow>
          {typeof eyebrow === 'string' ? (
            <SplitText
              tag="span"
              text={eyebrow}
              delay={20}
              duration={0.7}
              ease="power2.out"
              splitType="words"
              from={{ opacity: 0, y: 10 }}
              to={{ opacity: 1, y: 0 }}
              threshold={0.1}
              rootMargin="-40px"
              textAlign={align}
            />
          ) : (
            eyebrow
          )}
        </Eyebrow>
      )}
      {renderSplitH2(h2)}
      {p && (
        <SplitText
          tag="p"
          text={p}
          delay={12}
          duration={0.7}
          ease="power2.out"
          splitType="words"
          from={{ opacity: 0, y: 18 }}
          to={{ opacity: 1, y: 0 }}
          threshold={0.1}
          rootMargin="-40px"
          textAlign={align}
          className="section-head-p"
        />
      )}
    </div>
  );
}

/* ── Icon Tile ── */
export function IconTile({ children, size = 48, radius = 14, bg, color, style }) {
  return (
    <div
      className="icon-tile"
      style={{
        width: size,
        height: size,
        borderRadius: radius,
        background: bg,
        color: color,
        ...style,
      }}
    >
      <svg
        viewBox="0 0 24 24"
        fill="none"
        stroke="currentColor"
        strokeWidth={1.7}
        strokeLinecap="round"
        strokeLinejoin="round"
        style={{ width: size * 0.46, height: size * 0.46 }}
      >
        {children}
      </svg>
    </div>
  );
}

/* ── Btn ── */
export function Btn({ href, onClick, children, variant = 'primary', size, style, target, rel }) {
  const cls = `btn btn-${variant}${size === 'sm' ? ' btn-sm' : ''}`;
  if (href) return <a href={href} className={cls} style={style} target={target} rel={rel}>{children}</a>;
  return <button onClick={onClick} className={cls} style={style}>{children}</button>;
}

/* ── TruVue AI Logo Mark ── */
export function TruVueMark({ size = 30, spin, color = 'currentColor' }) {
  return (
    <svg
      viewBox="0 0 24 24"
      fill="none"
      stroke={color}
      strokeWidth={1.6}
      strokeLinecap="round"
      strokeLinejoin="round"
      style={{ width: size, height: size, animation: spin ? 'spin 22s linear infinite' : 'none', flexShrink: 0 }}
      aria-hidden="true"
    >
      <circle cx="12" cy="12" r="9.3" />
      {[0, 60, 120, 180, 240, 300].map(a => (
        <line key={a} x1="12" y1="12" x2="12" y2="4.2" transform={`rotate(${a} 12 12)`} />
      ))}
      <circle cx="12" cy="12" r="2" fill={color} stroke="none" />
    </svg>
  );
}

/* ── Counter ── */
import { useState, useEffect } from 'react';
export function Counter({ to, prefix = '', suffix = '' }) {
  const [val, setVal] = useState(0);
  const [ref, vis] = useReveal(0.5);
  useEffect(() => {
    if (!vis) return;
    const reduced = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    if (reduced) { setVal(to); return; }
    const dur = 1400;
    const start = performance.now();
    const tick = now => {
      const p = Math.min((now - start) / dur, 1);
      const eased = 1 - Math.pow(1 - p, 3);
      setVal(Math.round(eased * to));
      if (p < 1) requestAnimationFrame(tick);
    };
    requestAnimationFrame(tick);
  }, [vis, to]);
  return <span ref={ref}>{prefix}{val.toLocaleString('en-US')}{suffix}</span>;
}